import { useCallback, useEffect, useRef, useState } from 'react';

export type RecState = 'idle' | 'recording' | 'ready' | 'denied';

/** Record → play back, kept in memory for this session only (nothing is uploaded). */
export function useRecorder() {
  const [state, setState] = useState<RecState>('idle');
  const [url, setUrl] = useState<string | null>(null);
  const [seconds, setSeconds] = useState(0);
  const rec = useRef<MediaRecorder | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const started = useRef(0);

  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);
  useEffect(() => {
    if (state !== 'recording') return;
    const t = setInterval(() => setSeconds(Math.round((Date.now() - started.current) / 1000)), 300);
    return () => clearInterval(t);
  }, [state]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks: Blob[] = [];
      const r = new MediaRecorder(stream);
      r.ondataavailable = (e) => chunks.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setUrl(URL.createObjectURL(new Blob(chunks, { type: r.mimeType })));
        setState('ready');
      };
      r.start();
      rec.current = r;
      started.current = Date.now();
      setSeconds(0);
      setState('recording');
    } catch {
      setState('denied');
    }
  }, []);

  const stop = useCallback(() => rec.current?.stop(), []);

  const play = useCallback(() => {
    if (!url) return Promise.resolve();
    audio.current?.pause();
    audio.current = new Audio(url);
    return new Promise<void>((resolve) => {
      audio.current!.onended = () => resolve();
      void audio.current!.play().catch(() => resolve());
    });
  }, [url]);

  const reset = useCallback(() => {
    setUrl(null);
    setState('idle');
  }, []);

  return { state, url, seconds, start, stop, play, reset };
}
